/*
 * ACESO — koleksiyon ve arama sonuçları
 *
 * Filtre, sıralama ve "daha fazla göster" işlemleri sayfayı yenilemeden Section
 * Rendering API ile yapılır; JS kapalıyken aynı bağlantılar ve form normal sayfa
 * yüklemesiyle çalışır. Ürünler her zaman sunucuda filtrelenir.
 */
(() => {
  const theme = window.theme;
  const DENSITY_KEY = 'aceso:grid-density';

  class CollectionView extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.sectionId = this.dataset.sectionId;
      this.compareLimit = Number(this.dataset.compareLimit || 5);
      this.compared = [];
      this.compareMode = false;

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));

      /* Mobil filtre çekmecesi ve karşılaştırma penceresi <body>'ye taşındığı için
         olayları belge düzeyinde dinleriz. */
      this.onDocumentClick = (event) => {
        if (this.contains(event.target)) return;
        if (event.target.closest(`[data-facets-for="${CSS.escape(this.sectionId)}"]`)) this.onClick(event);
        if (event.target.closest('.collection-compare-tray, .compare-dialog')) this.onCompareClick(event);
      };
      this.onDocumentChange = (event) => {
        if (this.contains(event.target)) return;
        if (event.target.closest(`[data-facets-for="${CSS.escape(this.sectionId)}"]`)) this.onChange(event);
      };
      this.onDocumentInput = (event) => {
        if (this.contains(event.target)) return;
        if (event.target.closest(`[data-facets-for="${CSS.escape(this.sectionId)}"]`)) this.onInput(event);
      };
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('change', this.onDocumentChange);
      document.addEventListener('input', this.onDocumentInput);

      this.onPopState = () => this.update(window.location.href, { push: false });
      window.addEventListener('popstate', this.onPopState);

      this.applyDensity(theme.storage.read(DENSITY_KEY, 'standard'));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('change', this.onDocumentChange);
      document.removeEventListener('input', this.onDocumentInput);
      window.removeEventListener('popstate', this.onPopState);
      this.removeTray();
    }

    /* ---------------- Olaylar ---------------- */

    onClick(event) {
      const facetLink = event.target.closest('[data-facet-link]');
      if (facetLink) {
        event.preventDefault();
        this.update(facetLink.href);
        return;
      }

      const loadMore = event.target.closest('[data-load-more]');
      if (loadMore) {
        event.preventDefault();
        this.loadMore(loadMore);
        return;
      }

      const filterToggle = event.target.closest('[data-filter-toggle]');
      if (filterToggle) {
        this.toggleSidebar(filterToggle);
        return;
      }

      const density = event.target.closest('[data-density]');
      if (density) {
        this.applyDensity(density.dataset.density);
        theme.storage.write(DENSITY_KEY, density.dataset.density);
        return;
      }

      if (event.target.closest('[data-compare-switch]')) {
        this.setCompareMode(!this.compareMode);
        return;
      }

      const compareToggle = event.target.closest('[data-compare-toggle]');
      if (compareToggle) {
        const card = compareToggle.closest('product-card');
        if (card) this.toggleCompared(card);
      }
    }

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-sort]')) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', target.value);
        url.searchParams.delete('page');
        this.update(url.toString());
        return;
      }
      const form = target.closest('[data-facets-form]');
      if (form) this.update(this.urlFromForm(form));
    }

    onInput(event) {
      const range = event.target.closest('[data-price-range]');
      if (!range) return;
      const output = range.closest('fieldset').querySelector('[data-price-output]');
      if (output) output.textContent = theme.formatMoney(Number(range.value) * 100);
    }

    /* ---------------- Filtre ve sıralama ---------------- */

    urlFromForm(form) {
      const url = new URL(form.action, window.location.origin);
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (value === '') return;
        params.append(key, value);
      });
      /* Kaydırıcı en sağdaysa üst sınır yoktur. */
      form.querySelectorAll('[data-price-range]').forEach((range) => {
        if (Number(range.value) >= Number(range.max)) params.delete(range.name);
      });
      const current = new URL(window.location.href).searchParams;
      ['q', 'type', 'options[prefix]'].forEach((key) => {
        if (current.has(key) && !params.has(key)) params.set(key, current.get(key));
      });
      if (!params.has('sort_by') && current.has('sort_by')) params.set('sort_by', current.get('sort_by'));
      url.search = params.toString();
      return url.toString();
    }

    regions() {
      const overlays = document.querySelectorAll(`body > overlay-dialog[data-section-id$="${CSS.escape(this.sectionId)}"]`);
      const list = Array.from(this.querySelectorAll('[data-facets-region]'));
      overlays.forEach((overlay) => list.push(...overlay.querySelectorAll('[data-facets-region]')));
      return list;
    }

    async update(href, { push = true } = {}) {
      const url = new URL(href, window.location.origin);
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      this.setAttribute('aria-busy', 'true');
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const focused = document.activeElement;
      const focusedId = focused && focused.id;
      try {
        const doc = await theme.fetchDocument(fetchUrl.toString(), { signal: this.controller.signal });
        const fresh = new Map();
        doc.querySelectorAll('[data-facets-region]').forEach((element) => {
          if (!fresh.has(element.dataset.facetsRegion)) fresh.set(element.dataset.facetsRegion, element);
        });
        this.regions().forEach((region) => {
          const source = fresh.get(region.dataset.facetsRegion);
          if (source) region.innerHTML = source.innerHTML;
        });
        if (push) window.history.pushState({}, '', url.toString());
        this.applyDensity(theme.storage.read(DENSITY_KEY, 'standard'));
        this.applyCompareState();
        theme.favorites.sync(this);
        if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
        const count = this.querySelector('[data-facets-region="count"]');
        if (count) theme.announce(count.textContent.trim());
      } catch (error) {
        if (error.name !== 'AbortError') window.location.assign(url.toString());
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    async loadMore(link) {
      const wrapper = link.closest('[data-load-more-wrapper]');
      const grid = this.querySelector('[data-product-grid]');
      if (!grid || !wrapper) return;
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      link.setAttribute('aria-busy', 'true');
      try {
        const doc = await theme.fetchDocument(url.toString());
        const cards = Array.from(doc.querySelectorAll('[data-product-grid] > product-card'));
        const firstNew = cards[0];
        grid.append(...cards);
        const next = doc.querySelector('[data-load-more-wrapper]');
        if (next) wrapper.replaceWith(next);
        else wrapper.remove();
        this.applyCompareState();
        theme.favorites.sync(grid);
        if (firstNew) firstNew.querySelector('a')?.focus({ preventScroll: true });
      } catch (error) {
        window.location.assign(link.href);
      }
    }

    toggleSidebar(button) {
      const layout = this.querySelector('.collection-results-layout');
      const sidebar = this.querySelector('.collection-filter-sidebar');
      if (!layout || !sidebar) return;
      const open = sidebar.hidden;
      sidebar.hidden = !open;
      layout.classList.toggle('has-filter', open);
      button.setAttribute('aria-expanded', String(open));
      const label = button.querySelector('[data-filter-toggle-label]');
      if (label) label.textContent = open ? this.dataset.labelHide : this.dataset.labelShow;
    }

    applyDensity(density) {
      const grid = this.querySelector('[data-product-grid]');
      const value = density === 'relaxed' ? 'relaxed' : 'standard';
      if (grid) {
        grid.classList.toggle('collection-grid-standard', value === 'standard');
        grid.classList.toggle('collection-grid-relaxed', value === 'relaxed');
      }
      this.querySelectorAll('[data-density]').forEach((button) => {
        const active = button.dataset.density === value;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
    }

    /* ---------------- Karşılaştırma ---------------- */

    setCompareMode(on) {
      this.compareMode = on;
      if (!on) this.compared = [];
      this.querySelectorAll('[data-compare-switch]').forEach((button) => {
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-checked', String(on));
      });
      this.applyCompareState();
    }

    cardData(card) {
      const image = card.querySelector('.product-image-primary');
      const link = card.querySelector('.product-info a');
      return {
        handle: card.dataset.productHandle,
        url: card.dataset.productUrl,
        color: card.dataset.color || '',
        title: link ? link.textContent.trim() : '',
        image: image ? image.currentSrc || image.src : '',
        price: card.querySelector('.price strong')?.lastChild?.textContent.trim() || '',
        comparePrice: card.querySelector('.price del')?.lastChild?.textContent.trim() || '',
      };
    }

    toggleCompared(card) {
      const handle = card.dataset.productHandle;
      const index = this.compared.findIndex((item) => item.handle === handle);
      if (index > -1) this.compared.splice(index, 1);
      else if (this.compared.length < this.compareLimit) this.compared.push(this.cardData(card));
      this.applyCompareState();
    }

    applyCompareState() {
      const full = this.compared.length >= this.compareLimit;
      this.querySelectorAll('[data-compare-toggle]').forEach((button) => {
        const card = button.closest('product-card');
        const selected = this.compared.some((item) => item.handle === card.dataset.productHandle);
        button.hidden = !this.compareMode;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = full && !selected;
        button.textContent = selected
          ? button.dataset.labelSelected
          : full
            ? button.dataset.labelLimit
            : button.dataset.labelAdd;
      });
      this.renderTray();
    }

    removeTray() {
      if (this.tray) this.tray.remove();
      this.tray = null;
    }

    renderTray() {
      if (!this.compareMode || this.compared.length === 0) {
        this.removeTray();
        return;
      }
      if (!this.tray) {
        const template = this.querySelector('[data-compare-tray-template]');
        if (!template) return;
        this.tray = template.content.firstElementChild.cloneNode(true);
        document.body.append(this.tray);
      }
      const count = this.tray.querySelector('[data-compare-count]');
      count.textContent = count.dataset.template
        .replace('[count]', String(this.compared.length))
        .replace('[limit]', String(this.compareLimit));
      const list = this.tray.querySelector('[data-compare-products]');
      list.replaceChildren(
        ...this.compared.map((item) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.compareRemove = item.handle;
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          const name = document.createElement('span');
          name.textContent = item.title;
          const cross = document.createElement('b');
          cross.setAttribute('aria-hidden', 'true');
          cross.textContent = '×';
          button.append(image, name, cross);
          return button;
        }),
      );
      const open = this.tray.querySelector('[data-compare-open]');
      const ready = this.compared.length >= 2;
      open.disabled = !ready;
      open.textContent = ready ? open.dataset.label : open.dataset.labelMin;
    }

    onCompareClick(event) {
      const remove = event.target.closest('[data-compare-remove]');
      if (remove) {
        this.compared = this.compared.filter((item) => item.handle !== remove.dataset.compareRemove);
        this.applyCompareState();
        if (event.target.closest('.compare-dialog')) this.renderDialog();
        return;
      }
      if (event.target.closest('[data-compare-clear]')) {
        this.compared = [];
        this.applyCompareState();
        return;
      }
      if (event.target.closest('[data-compare-open]')) this.openDialog(event.target.closest('[data-compare-open]'));
    }

    get dialogOverlay() {
      return document.getElementById(`CompareDialog-${this.sectionId}`);
    }

    async openDialog(opener) {
      const overlay = this.dialogOverlay;
      if (!overlay) return;
      await this.renderDialog();
      overlay.open(opener);
    }

    /** Beden, kategori ve stok bilgisi kartta yoktur; ürün JSON'undan tamamlanır. */
    async renderDialog() {
      const overlay = this.dialogOverlay;
      const grid = overlay.querySelector('[data-compare-grid]');
      if (this.compared.length === 0) {
        overlay.close();
        return;
      }
      const labels = grid.dataset;
      const details = await Promise.all(
        this.compared.map(async (item) => {
          try {
            const response = await fetch(`${item.url.split('?')[0]}.js`);
            return response.ok ? await response.json() : null;
          } catch (error) {
            return null;
          }
        }),
      );

      const row = (term, ...content) => {
        const wrapper = document.createElement('div');
        const dt = document.createElement('dt');
        dt.textContent = term;
        const dd = document.createElement('dd');
        dd.append(...content);
        wrapper.append(dt, dd);
        return wrapper;
      };

      grid.replaceChildren(
        ...this.compared.map((item, index) => {
          const product = details[index];
          const article = document.createElement('article');

          const remove = document.createElement('button');
          remove.className = 'compare-dialog-remove';
          remove.type = 'button';
          remove.dataset.compareRemove = item.handle;
          remove.setAttribute('aria-label', labels.labelRemove.replace('[title]', item.title));
          remove.textContent = '×';

          const link = document.createElement('a');
          link.href = item.url;
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = item.title;
          const heading = document.createElement('h3');
          heading.textContent = item.title;
          link.append(image, heading);

          const list = document.createElement('dl');
          const price = document.createElement('strong');
          price.textContent = item.price;
          const priceContent = [price];
          if (item.comparePrice) {
            const old = document.createElement('del');
            old.textContent = item.comparePrice;
            priceContent.unshift(old);
          }
          const sizes = product
            ? [...new Set(product.variants.map((variant) => variant.options[variant.options.length - 1]))].join(', ')
            : '—';
          const available = product ? product.available : true;
          list.append(
            row(labels.labelPrice, ...priceContent),
            row(labels.labelColor, item.color || '—'),
            row(labels.labelCategory, (product && product.type) || '—'),
            row(labels.labelSizes, sizes || '—'),
            row(labels.labelStock, available ? labels.labelInStock : labels.labelSoldOut),
          );

          const view = document.createElement('a');
          view.className = 'button button-dark button-full';
          view.href = item.url;
          view.textContent = labels.labelView;

          article.append(remove, link, list, view);
          return article;
        }),
      );
    }
  }

  customElements.define('collection-view', CollectionView);
})();
